<!--
  Login Component
-->
<template>
  <div class="relative flex min-h-screen flex-col overflow-x-hidden gradient-bg" style='font-family: Inter, "Noto Sans", sans-serif;'>
    <div class="layout-container flex h-full grow flex-col items-center justify-center">
      <div class="w-full max-w-md bg-white dark:bg-slate-800 shadow-xl rounded-xl p-8 md:p-12 space-y-8">
        <div class="absolute top-4 right-4">
          <dark-mode-toggle />
        </div>
        <!-- Header -->
        <header class="flex flex-col items-center justify-center text-center">
          <img 
          src="@/assets/images/logo.png" 
          alt="Portinos Logo" 
          class="h-10 w-auto"
        /><br>
          <div class="flex items-center gap-3 text-[#111518] dark:text-orange-500">
            <h1 class="text-[#111518] dark:text-slate-100 tracking-tight text-3xl font-bold leading-tight pt-6 pb-2">Herramienta de Estimaciones Portinos</h1>
          </div>
          <p class="text-slate-600 dark:text-slate-400 text-base">Por favor, ingresa tus credenciales para acceder a tu cuenta.</p>
        </header>

        <!-- Login Form -->
        <form @submit.prevent="handleLogin" class="space-y-6">
          <!-- Email Field -->
          <div>
            <label class="block text-[#111518] dark:text-slate-300 text-sm font-medium leading-normal pb-1.5" for="email">Email</label>
            <input
              class="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-[#111518] dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400 border border-[#dbe1e6] dark:border-slate-600 bg-white dark:bg-slate-700 h-12 placeholder:text-[#60768a] dark:placeholder:text-slate-400 p-3 text-base font-normal leading-normal"
              id="email"
              name="email"
              placeholder="Ingresa tu email"
              type="email"
              v-model="form.email"
              :disabled="isLoading"
              required
            />
            <p v-if="errors.email" class="mt-1 text-sm text-red-500 dark:text-red-400">{{ errors.email }}</p>
          </div>

          <!-- Password Field -->
          <div>
            <label class="block text-[#111518] dark:text-slate-300 text-sm font-medium leading-normal pb-1.5" for="password">Contraseña</label>
            <input
              class="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-[#111518] dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400 border border-[#dbe1e6] dark:border-slate-600 bg-white dark:bg-slate-700 h-12 placeholder:text-[#60768a] dark:placeholder:text-slate-400 p-3 text-base font-normal leading-normal"
              id="password"
              name="password"
              placeholder="Ingresa tu contraseña"
              type="password"
              v-model="form.password"
              :disabled="isLoading"
              required
            />
            <p v-if="errors.password" class="mt-1 text-sm text-red-500 dark:text-red-400">{{ errors.password }}</p>
          </div>

          <!-- Forgot Password Link -->
          <div class="flex items-center justify-end">
            <a class="text-[#0b80ee] hover:text-[#005cb9] dark:text-orange-500 dark:hover:text-orange-400 text-sm font-medium leading-normal underline" href="#" @click.prevent="forgotPassword">¿Olvidaste tu contraseña?</a>
          </div>

          <!-- Error Message -->
          <div v-if="authError" class="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-500/50 rounded-lg p-4">
            <div class="flex">
              <p class="text-sm text-red-700 dark:text-red-400">{{ authError }}</p>
            </div>
          </div>

          <!-- Login Button -->
          <div>
            <button
              class="flex w-full min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-4 bg-orange-600 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-600 text-white text-base font-semibold leading-normal tracking-[0.015em] transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 dark:focus:ring-orange-400 disabled:opacity-50 disabled:cursor-not-allowed"
              type="submit"
              :disabled="isLoading || !isFormValid"
            >
              <span v-if="isLoading" class="flex items-center">
                <div class="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                Iniciando sesión...
              </span>
              <span v-else class="truncate">Ingresar</span>
            </button>
          </div>

          <!-- Register Link -->
          <div class="text-center">
            <p class="text-sm text-slate-600 dark:text-slate-400">
              No tienes una cuenta?
              <router-link
                to="/register"
                class="font-medium text-orange-600 hover:text-orange-700 dark:text-orange-500 dark:hover:text-orange-400"
              >
                Regístrate aquí
              </router-link>
            </p>
          </div>
        </form>

      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth } from '@/composables/useAuth';
import DarkModeToggle from '@/components/ui/DarkModeToggle.vue'; // Added import

export default {
  name: 'LoginComponent',
  components: { // Added components option
    DarkModeToggle
  },
  setup() {
    const router = useRouter();
    const { login, isLoading, authError, isAuthenticated } = useAuth();

    // Form state
    const form = ref({
      email: '',
      password: ''
    });

    const errors = ref({});
    const debugMode = ref(process.env.NODE_ENV === 'development');
    const apiUrl = ref(process.env.VUE_APP_API_URL || 'http://localhost:3001');

    // Validation
    const validateForm = () => {
      errors.value = {};

      // Email validation
      if (!form.value.email) {
        errors.value.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
        errors.value.email = 'Invalid email format';
      }

      // Password validation
      if (!form.value.password) {
        errors.value.password = 'Password is required';
      } else if (form.value.password.length < 6) {
        errors.value.password = 'Password must be at least 6 characters';
      }

      return Object.keys(errors.value).length === 0;
    };

    const isFormValid = computed(() => {
      return form.value.email && 
             form.value.password && 
             /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email) &&
             form.value.password.length >= 6;
    });

    // Handle login
    const handleLogin = async () => {
      console.log('🔐 Login button clicked'); // Debug log
      
      if (!validateForm()) {
        console.log('❌ Form validation failed', errors.value);
        return;
      }

      console.log('✅ Attempting login with:', { email: form.value.email }); // Don't log password

      try {
        const result = await login({
          email: form.value.email,
          password: form.value.password
        });

        console.log('📊 Login result:', result);

        if (result.success) {
          // Show brief loading state, then redirect
          const redirectTo = router.currentRoute.value.query.redirect || '/budgets';
          console.log('🔄 Redirecting to:', redirectTo);
          
          // Small delay to show success state, then redirect
          setTimeout(async () => {
            await router.push(redirectTo);
          }, 500);
        }
      } catch (error) {
        console.error('💥 Login error:', error);
      }
    };

    // Forgot password handler
    const forgotPassword = () => {
      alert('Forgot password functionality not implemented yet.');
    };

    // Redirect if already authenticated
    onMounted(() => {
      console.log('🚀 LoginComponent mounted, authenticated:', isAuthenticated.value);
      if (isAuthenticated.value) {
        router.push('/budgets');
      }
    });

    return {
      form,
      errors,
      isFormValid,
      isLoading,
      authError,
      handleLogin,
      forgotPassword,
      debugMode,
      apiUrl
    };
  }
};
</script>

<style scoped>
.gradient-bg {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
}

.dark .gradient-bg {
  background: linear-gradient(135deg, theme('colors.slate.800') 0%, theme('colors.slate.900') 100%);
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
